/**
 * Resignation of Secretary (M32107) 필드 타입/라벨.
 * MVP SecretaryResignationDoc 의 field-key 와 1:1.
 * CddDocVarField 재사용을 위해 CddFieldType 호환.
 */
import type { CddFieldType } from '../cdd/cddFields';

export type ResignFieldType = CddFieldType | 'clause';

const COMMON_KEYS = [
  'company_name',
  'company_registration_no',
  'company_address',
  'constitution_clause_number',
  'resolution_date',
  'letter_date',
] as const;

/** secretary 행별 키 suffix (sec{id}_*) */
export const SEC_SUFFIXES = [
  'full_name',
  'ic_passport_no',
  'residential_address',
  'date_of_cessation',
  'date_of_appointment',
  'license_type',
  'license_number',
  'ssm_pc_no',
] as const;

export function secKey(id: number, suffix: (typeof SEC_SUFFIXES)[number]): string {
  return `sec${id}_${suffix}`;
}

export function allKeysForRows(rowIds: number[]): string[] {
  const keys: string[] = [...COMMON_KEYS];
  for (const id of rowIds) {
    for (const s of SEC_SUFFIXES) keys.push(secKey(id, s));
  }
  return keys;
}

/** Studio 진행률용 — Third Schedule 이면 정관번호 불필요하므로 제외 */
export function progressKeysForRows(
  rowIds: number[],
  basis: ResolutionBasis | string | null | undefined,
): string[] {
  const keys = allKeysForRows(rowIds);
  if (basis === 'third_schedule') {
    return keys.filter((k) => k !== 'constitution_clause_number');
  }
  return keys;
}

const OVERRIDES: Record<string, string> = {
  company_name: 'Company Name',
  company_registration_no: 'Company Registration No.',
  company_address: 'Company Address',
  constitution_clause_number: 'Clause / Article No.',
  resolution_date: 'Resolution Date',
  letter_date: 'Letter Date',
  full_name: 'Full Name',
  ic_passport_no: 'NRIC / Passport No.',
  residential_address: 'Residential Address',
  date_of_cessation: 'Date of Cessation',
  date_of_appointment: 'Date of Appointment',
  license_type: 'License Type',
  license_number: 'License No.',
  ssm_pc_no: 'SSM PC No.',
};

export function labelize(key: string): string {
  if (OVERRIDES[key]) return OVERRIDES[key];
  const bare = key.replace(/^sec\d+_/, '');
  if (OVERRIDES[bare]) return OVERRIDES[bare];
  return bare.split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

export function typeOf(key: string): ResignFieldType {
  if (/(^|_)(date|dob|cessation|appointment)$/.test(key) || /_date$/.test(key) || key.endsWith('date_of_cessation') || key.endsWith('date_of_appointment')) return 'date';
  if (key === 'company_name') return 'entity';
  if (key === 'constitution_clause_number') return 'clause';
  if (/full_name$/.test(key)) return 'person';
  if (/_address$/.test(key) || key === 'company_address' || key.endsWith('residential_address')) return 'address';
  return 'text';
}

export const REASONS = [
  'Resignation',
  'Expiry of term',
  'Removal',
  'Vacate office',
  'Deceased',
] as const;

export type ResignReason = (typeof REASONS)[number];
export type DirectorMode = 'sole' | 'multiple';
export type ResolutionBasis = 'constitution' | 'articles' | 'third_schedule';
export type IdKind = 'malaysian' | 'foreigner';

/** formData 기준 Third Schedule 여부 (Edit filled/total · Studio % 공통) */
export function isResignThirdScheduleBasis(formData: Record<string, any> | null | undefined): boolean {
  const fd = formData || {};
  if (fd.basis === 'third_schedule' || fd.governing_basis === 'third_schedule') return true;
  if (fd.third_schedule === true || fd.third_schedule === 'true' || fd.third_schedule === 1) return true;
  if (fd.resolution_basis_third_schedule === true || fd.resolution_basis_third_schedule === 'true') return true;
  if (fd.basisThirdSchedule === true || fd.basisThirdSchedule === 'true') return true;
  return false;
}

const CLAUSE_VAR_KEYS = new Set([
  'constitution_clause_number',
  'constitution_clause_no',
  'constitution_clause',
  'clause_number',
  'article_number',
  'article_no',
]);

function isClauseNumberVar(varKey?: string, varLabel?: string): boolean {
  const k = String(varKey || '').trim().toLowerCase();
  const lab = String(varLabel || '').trim().toLowerCase();
  if (k && CLAUSE_VAR_KEYS.has(k)) return true;
  if (/constitution_clause|clause_number|article_no|article_number/.test(k)) return true;
  if (/clause\s*\/?\s*article|article\s*no|clause\s*no|constitution\s*clause/.test(lab)) return true;
  return false;
}

/** Edit 변수 목록 — Third Schedule 이면 정관번호 변수 제거 (카운트·폼 동일) */
export function applyResignDocumentVariableFilter<T extends { varKey?: string; varLabel?: string }>(input: {
  variables: T[];
  repeatingSections?: any[];
  formData?: Record<string, any> | null;
}): { variables: T[]; repeatingSections: any[] } {
  const sections = Array.isArray(input.repeatingSections) ? input.repeatingSections : [];
  const vars = Array.isArray(input.variables) ? input.variables : [];
  if (!isResignThirdScheduleBasis(input.formData)) {
    return { variables: vars, repeatingSections: sections };
  }
  return {
    variables: vars.filter((v) => !isClauseNumberVar(v?.varKey, v?.varLabel)),
    repeatingSections: sections,
  };
}

/** templateName 매칭 — Preset 에 붙은 패키지 문서 */
export const DOC_SHEET_PATTERNS = {
  resolution: /circular\s*resolution|directors.?\s*circular/i,
  letter: /resignation\s*letter|letter.*resign/i,
  section58: /section\s*58|58i|cessation.*secretary|secretary.*cessation/i,
} as const;
